import { Component, computed, effect, EventEmitter, Input, OnDestroy, OnInit, Output, Signal, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subject, Subscription, takeUntil } from 'rxjs';
import { CountriesService } from '../services/countries.service';
import { CardHeader } from '../../../models/card-header';
import { CardSearchService } from '../services/card-search.service';
import { debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { cardLimits } from '../../../constants/card-limits';
import { InteractionEnum } from '../../../enum/interaction.enum';
import { GeoSearchService } from '../services/geo-search.service';
import { GeoSearch } from '../../../models/geo-search';
import { CardTypeEnum } from '../../../enum/card-type.enum';
import { ConstraintEnum } from '../../../enum/constraint.enum';
import { MatError, MatHint, MatInput } from '@angular/material/input';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { IconComponent } from '../icon/icon.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { selectContactEditorCard } from '../../../store/contact-editor/contact-editor.state';
import { contactEditorSetCard } from '../../../store/contact-editor/contact-editor.actions';
import { Card } from '../../../models/card';
import {
    shipmentEditorSetShipmentDate,
    shipmentEditorUpdateBroker,
    shipmentEditorUpdateDestination,
    shipmentEditorUpdateShipper, shipmentEditorUpdateSoldTo
} from '../../../store/shipment-editor/shipment-editor.actions';
import {
    selectShipmentEditorBroker,
    selectShipmentEditorCategory,
    selectShipmentEditorDate,
    selectShipmentEditorDestination,
    selectShipmentEditorShipper, selectShipmentEditorSoldTo, selectShipmentEditorViolations
} from '../../../store/shipment-editor/shipment-editor.state';
import { emptyCard } from '../../../constants/empty-card';
import { ProvinceNamePipe } from '../pipes/province-name.pipe';
import { CountryFieldComponent } from '../country-field/country-field.component';
import { TimeFieldComponent } from '../time-field/time-field.component';
import { ShipmentCategoryEnum } from '../../../enum/shipment-category.enum';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { Clock } from '../../../utils/clock';
import { Violation } from '../../../models/violation';

@Component({
    selector: 'app-card-form',
    templateUrl: './card-form.component.html',
    styleUrls: [ './card-form.component.scss' ],
    imports: [ ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatAutocompleteTrigger, MatSuffix, MatProgressSpinner, IconComponent,
        MatAutocomplete, MatOption, MatSelect, AsyncPipe, ProvinceNamePipe, CountryFieldComponent, TimeFieldComponent, MatDatepickerToggle, MatDatepicker, MatDatepickerInput, MatError, MatHint ]
})
export class CardFormComponent implements OnInit, OnDestroy {

    @Input()
    cardType: CardTypeEnum;

    @Input()
    set card(card: Card) {
        this.formGroup.patchValue(card, { emitEvent: false });
    }

    get card(): Partial<Card> {
        return this.formGroup.value;
    }

    @ViewChild('organizationInput', { read: MatInput })
    organizationInput: MatInput;

    @ViewChild('provinceInput', { read: MatInput })
    provinceInput: MatInput;

    @Output()
    updated: EventEmitter<InteractionEnum> = new EventEmitter<InteractionEnum>();

    @Output()
    change: EventEmitter<Partial<Card>> = new EventEmitter<Partial<Card>>();

    private readonly destroy$ = new Subject<void>();

    countryValue = new BehaviorSubject<string>('');
    provinceValue = new BehaviorSubject<string>('');

    cardLimits = cardLimits;
    constraintEnum = ConstraintEnum;
    cardTypeEnum = CardTypeEnum;

    violations = this.store.selectSignal(selectShipmentEditorViolations);

    attnControl: FormControl = new FormControl('', { validators: [ this.violationValidator('attn') ]});
    organizationControl: FormControl = new FormControl('', { validators: [ this.violationValidator('organization') ]});
    countryControl: FormControl = new FormControl('', { updateOn: 'blur', validators: [ this.violationValidator('country') ] });
    provinceControl: FormControl = new FormControl('', { validators: [ this.violationValidator('province') ]});
    postalCodeControl: FormControl = new FormControl('', { validators: [ this.violationValidator('postalCode') ]});
    cityControl: FormControl = new FormControl('', { validators: [ this.violationValidator('city') ]});
    street1Control: FormControl = new FormControl('', { validators: [ this.violationValidator('street1') ]});
    street2Control: FormControl = new FormControl('', { validators: [ this.violationValidator('street2') ]});
    street3Control: FormControl = new FormControl('', { validators: [ this.violationValidator('street3') ]});
    telephoneControl: FormControl = new FormControl('', { validators: [ this.violationValidator('telephone') ]});
    emailControl: FormControl = new FormControl('', { validators: [ this.violationValidator('email') ]});
    residentialFlagControl: FormControl = new FormControl('');
    openTimeControl: FormControl = new FormControl('');
    closeTimeControl: FormControl = new FormControl('');
    formGroup = new FormGroup({
        attn: this.attnControl,
        organization: this.organizationControl,
        country: this.countryControl,
        province: this.provinceControl,
        postalCode: this.postalCodeControl,
        city: this.cityControl,
        street1: this.street1Control,
        street2: this.street2Control,
        street3: this.street3Control,
        telephone: this.telephoneControl,
        email: this.emailControl,
        residentialFlag: this.residentialFlagControl,
        openTime: this.openTimeControl,
        closeTime: this.closeTimeControl,
    });

    shipmentDateControl = new FormControl('', { nonNullable: true, updateOn: 'blur' });

    cityAutocomplete: MatAutocomplete;

    countryDisplay: (id: string) => string;
    provinceDisplay: (id: string) => string;

    validCountry = '';
    validProvince = '';

    filteredCountriesList: string[];
    filteredCountries$: Observable<string[]>;
    filteredProvinces$: Observable<string[]>;
    provinceControlVisible$: Observable<boolean>;
    postalCodeControlVisible$: Observable<boolean>;
    captionStateZip$: Observable<boolean>;
    cardSearch$: Observable<CardHeader[]>;
    cardSearchBusy$: Observable<boolean>;
    cardSearchFocused$: Subject<boolean> = new Subject<boolean>();
    citySearch$: Observable<GeoSearch[]>;
    postalCodeChange$: Subject<any>;
    change$: Subject<void>;
    provinceCaption$: Observable<string>;
    postalCodeCaption$: Observable<string>;

    countrySearchTerm = '';
    countryFocusValue = null;

    source$: Observable<Card>;

    stateSubscription: Subscription;
    formSubscription: Subscription;

    showTimes$: Observable<boolean> = of(false);
    showDate$: Observable<boolean> = of(false);

    static getViolationPath(cardType: CardTypeEnum, name: string): string {
        switch (cardType) {
            case CardTypeEnum.SHIPPER:
                return `shipment.shipper.${name}`;
            case CardTypeEnum.DESTINATION:
                return `shipment.destination.${name}`;
            case CardTypeEnum.BROKER:
                return `shipment.broker.${name}`;
            case CardTypeEnum.SOLD_TO:
                return `shipment.soldTo.${name}`;
        }
    }
    constructor(private store: Store<AppState>, private countries: CountriesService, private cardSearchService: CardSearchService,
                private geoSearchService: GeoSearchService) {
        this.cardSearchBusy$ = this.cardSearchService.busy$.pipe(
            withLatestFrom(this.cardSearchFocused$),
            map(([ busy, focused ]) => busy && focused)
        );
        this.postalCodeChange$ = new Subject<any>();
        this.change$ = new Subject<any>();
        this.filteredProvinces$ = this.change$.pipe(
            startWith(0),
            filter(() => this.formGroup && !!this.formGroup.get('country')),
            map(() => this.countries.getProvinces(this.formGroup.get('country').value).map(province => province.code))
        );
        this.filteredCountries$ = this.countryControl.valueChanges.pipe(
            startWith(''),
            map(() => this.countries.countryFilter(this.countrySearchTerm))
        );
        this.filteredCountriesList = this.countries.countryFilter('');

        effect(() => {
            this.violations();

            for (const control of Object.values(this.formGroup.controls)) {
                control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
                control.markAsTouched();
            }
        });
    }

    countryKeypress(key: any): void {
        this.countrySearchTerm = (this.countrySearchTerm + key).toUpperCase();
        const matches = this.countries.countryFilter(this.countrySearchTerm);
        this.filteredCountriesList = matches;
        if (matches.length > 0) {
            this.countryControl.setValue(matches[0]);
        }
    }

    /*
    countryBlur(): void {
        let countryCode = this.countryInput.value.toUpperCase();
        if (!this.countries.isCountryCode(countryCode)) {
            const list = this.countries.countryFilter(countryCode);
            if (list.length > 0) {
                countryCode = list[0];
            } else {
                countryCode = this.countryControl.value;
            }
        }
        if (countryCode !== this.countryInput.value) {
            this.countryControl.setValue(countryCode);
            this.countryValue.next(countryCode);
        }
    }

     */

    provinceBlur(): void {
        let provinceCode = this.provinceInput.value.toUpperCase();
        if (!this.countries.isProvinceCode(this.countryControl.value, provinceCode)) {
            const list = this.countries.provinceFilter(this.countryControl.value, provinceCode);
            if (list.length > 0) {
                provinceCode = list[0];
            } else {
                provinceCode = this.provinceControl.value;
            }
        }
        if (provinceCode !== this.provinceInput.value) {
            this.provinceControl.setValue(provinceCode);
            this.provinceValue.next(provinceCode);
        }
    }

    postalCodeBlur(): void {
        this.postalCodeChange$.next(0);
    }

    countrySelection(): void {
        if (!this.countrySearchTerm) {
            this.userChange();
        }
    }

    countryFocus(): void {
        this.countryFocusValue = this.countryControl.value;
        this.countrySearchTerm = '';
    }

    cardUpdated(card: Partial<Card>): void {
        card.country = this.countries.resolveCountryCode(card.country, this.validCountry);
        card.attn = card.attn?.toUpperCase();
        card.organization = card.organization?.toUpperCase();
        card.city = card.city?.toUpperCase();
        card.postalCode = card.postalCode?.toUpperCase();
        card.street1 = card.street1?.toUpperCase();
        card.street2 = card.street2?.toUpperCase();
        card.street3 = card.street3?.toUpperCase();
        switch (this.cardType) {
            case CardTypeEnum.CONTACT:
                this.store.dispatch(contactEditorSetCard({ card }));
                break;
            case CardTypeEnum.SHIPPER:
                this.store.dispatch(shipmentEditorUpdateShipper({ card }));
                break;
            case CardTypeEnum.DESTINATION:
                this.store.dispatch(shipmentEditorUpdateDestination({ card }));
                break;
            case CardTypeEnum.BROKER:
                this.store.dispatch(shipmentEditorUpdateBroker({ card }));
                break;
            case CardTypeEnum.SOLD_TO:
                this.store.dispatch(shipmentEditorUpdateSoldTo({ card }));
                break;
        }
        this.change.emit(card);
    }

    ngOnInit(): void {
        switch (this.cardType) {
            case CardTypeEnum.CONTACT:
                this.source$ = this.store.select(selectContactEditorCard);
                this.showTimes$ = of(true);
                break;
            case CardTypeEnum.SHIPPER:
                this.source$ = this.store.select(selectShipmentEditorShipper);
                this.showTimes$ = this.store.select(selectShipmentEditorCategory).pipe(
                    map(category => category === ShipmentCategoryEnum.LTL)
                );
                this.showDate$ = this.showTimes$.pipe();
                break;
            case CardTypeEnum.DESTINATION:
                this.source$ = this.store.select(selectShipmentEditorDestination);
                this.showTimes$ = this.store.select(selectShipmentEditorCategory).pipe(
                    map(category => category === ShipmentCategoryEnum.LTL)
                );
                break;
            case CardTypeEnum.BROKER:
                this.source$ = this.store.select(selectShipmentEditorBroker);
                this.showTimes$ = of(false);
                break;
            case CardTypeEnum.SOLD_TO:
                this.source$ = this.store.select(selectShipmentEditorSoldTo);
                this.showTimes$ = of(false);
                break;
            default:
                this.showTimes$ = of(true);
                this.source$ = this.formGroup.valueChanges.pipe(
                    startWith(this.card),
                    map((card) => ({ ...emptyCard, ...card }))
                );
                break;
        }
        this.stateSubscription = this.source$.subscribe(card => {
            this.validCountry = card.country;
            this.countryValue.next(card.country);
            this.provinceValue.next(card.province);
            this.formGroup.patchValue(card, { emitEvent: false });
            this.formGroup.markAllAsDirty();
            this.formGroup.markAllAsTouched();
        });
        this.formSubscription = this.formGroup.valueChanges.subscribe(card => this.cardUpdated(card));

        this.filteredCountries$ = this.countryValue.pipe(
            tap(value => console.log('country value', value)),
            map(value => this.countries.countryFilter(value))
        );
        this.filteredProvinces$ = this.provinceValue.pipe(
            tap(value => console.log('province value', value)),
            map(value => this.countries.provinceFilter(this.countryControl.value, value))
        );

        this.citySearch$ = this.postalCodeChange$.pipe(
            map(() => this.postalCodeControl.value),
            distinctUntilChanged(),
            switchMap((postalCode) => this.geoSearchService.cityLookup(postalCode, this.countryControl.value)),
            tap((data) => {
                if (data.length === 1 && data[0].city) {
                    this.cityControl.setValue(data[0].city);
                    this.provinceControl.setValue(data[0].province);
                    this.userChange();
                } else if (data.length > 1) {
                }
            })
        );

        this.provinceControlVisible$ = this.source$.pipe(
            map(card => this.countries.displayProvincesFor(card.country))
        );

        this.postalCodeControlVisible$ = this.source$.pipe(
            map(card => this.countries.displayPostalCodesFor(card.country))
        );

        this.captionStateZip$ = this.source$.pipe(
            map(card => card.country === 'US')
        );

        this.provinceCaption$ = this.source$.pipe(
            map(card => card.country === 'US' ? $localize`State` : $localize`Province`)
        );

        this.postalCodeCaption$ = this.source$.pipe(
            map(card => card.country === 'US' ? $localize`Zip Code` : $localize`Postal Code`)
        );

        this.countryDisplay = (id) => this.countries.countryName(id);
        this.provinceDisplay = (id) => this.countries.provinceName(this.countryControl.value, id);

        this.cardSearch$ = this.organizationControl.valueChanges.pipe(
            filter(() => this.organizationInput?.focused),  // we don't want to search unless the user is focused and typing
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((value) => this.cardSearchService.query(value))
        );

        this.store.select(selectShipmentEditorDate).pipe(takeUntil(this.destroy$))
            .subscribe(date => {
                if (date !== this.shipmentDateControl.value) {
                    this.shipmentDateControl.setValue(date, { emitEvent: false });
                }
            });

        this.shipmentDateControl.valueChanges.pipe(
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(date => {
            const shipmentDate = Clock.parseToIso(date);
            this.store.dispatch(shipmentEditorSetShipmentDate({ shipmentDate }));
        });
    }

    ngOnDestroy(): void {
        if (this.stateSubscription) {
            this.stateSubscription.unsubscribe();
            this.stateSubscription = null;
        }
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
            this.formSubscription = null;
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    onCardSearchSelection($event: MatAutocompleteSelectedEvent): void {
        this.organizationControl.setValue('', { emitEvent: false });
        this.cardSearchService.retrieve($event.option.value).pipe(take(1)).subscribe((card) => {
            this.cardUpdated(card);
        });
    }

    onCitySearchSelection($event: MatAutocompleteSelectedEvent): void {
        if ($event.option.value.province) {
            this.provinceControl.setValue($event.option.value.province);
        }
        if (this.cityControl.value !== $event.option.value.city) {
            this.cityControl.setValue($event.option.value.city);
            this.userChange();
        }
    }

    onCountrySearchSelection($event: MatAutocompleteSelectedEvent): void {
        this.countryControl.setValue($event.option.value);
        this.userChange();
    }

    onProvinceSearchSelection($event: MatAutocompleteSelectedEvent): void {
        this.provinceControl.setValue($event.option.value);
        this.userChange();
    }

    onProvinceBlur(): void {
        // we do this asynchronously because the blur occurs before clicking on the autocomplete item, plus it lets the user
        // see that a change was made
        setTimeout(() => {
            const id = this.countries.resolveProvinceCode(this.countryControl.value, this.provinceControl.value, this.validProvince);
            this.provinceControl.setValue(id);
        }, 200);
    }

    userChange(): void {
        this.updated.emit(InteractionEnum.DEFAULT);
    }

    countryLabel(code: string): string {
        return this.countries.countryName(code);
    }

    getErrorMessage(controlName: string): string {
        const control = this.formGroup.get(controlName);
        return control.getError('required');
    }

    /*
    countryChanged(): void {
        this.countryValue.next(this.countryInput.value);
    }

     */

    provinceChanged(): void {
        this.provinceValue.next(this.provinceInput.value);
    }

    private violationValidator(fieldName: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const path = CardFormComponent.getViolationPath(this.cardType, fieldName);
            const violationsForField = this.violations().filter(v => v.path.startsWith(path));

            return violationsForField.length
                ? { violations: violationsForField }
                : null;
        };
    }
}
